import React, { useEffect, useRef, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { MdDeleteForever, MdEdit, MdBusiness, MdPeople } from "react-icons/md";
import { Link } from "react-router-dom";
import { useSelector } from 'react-redux';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Form } from "@unform/web";
import { toast } from "react-toastify";
import * as Yup from "yup";
import DivDiagonal from "../../components/DivDiagonal";
import CustomButton from "../../components/Form/Button";
import DeleteCompanyModal from "./DeleteCompanyModal";
import Input from "../../components/Form/Input";
import messageTypes from "../../constants/MessagesTypes";
import { getCompanies, deleteCompany } from "../../services/Company";
import {
  CompanyWrapper,
  PageHeader,
  NewCompanyButton,
  CompanyCard,
  FilterSection,
  FilterTitle,
  FormWrapper,
  StyledFilterFormWrapper,
  ContentArea,
  StyledTableContainer,
  ActionButtons,
  PaginationInfo,
  PaginationButton,
  LoadingButton,
  EmptyState,
  TableWrapper,
  FilterRow,
  FilterInputWrapper,
  FilterButtonWrapper
} from "./styles";

export default function CompanyList() {
  const [loading, setLoading] = useState(false);
  const [deleteCompanyId, setDeleteCompanyId] = useState(null);
  const [total, setTotal] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [page, setPage] = useState(1);
  const { isAdmin } = useSelector((state) => state.CompanyReducer || { isAdmin: false });

  const formRef = useRef(null);

  const [filter, setFilter] = useState({
    stringSearch: '',
  });

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Apenas administradores podem acessar esta página.');
      return;
    }

    setLoading(true);

    getCompanies({
      page: page,
      per_page: 25,
      stringSearch: filter.stringSearch,
    }).then((data) => {
      if (!data || data === '') {
        return;
      }

      setTotal(data.data.total);
      setCompanies(data.data.data || []);
      setLoading(false);
    });

    return function cleanup() {
      setCompanies([]);
    };
  }, [page, deleteCompanyId, filter, isAdmin]);

  async function handleSubmit(data) {
    try {
      formRef.current.setErrors({});
      const schema = Yup.object().shape({});

      await schema.validate(data, {
        abortEarly: false,
      });

      setFilter({
        stringSearch: data.stringSearch,
      });
      setPage(1);

    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
      }
    }
  }

  function handleDelete(id) {
    setDeleteCompanyId(id);
  }

  async function handleConfirmDelete() {
    try {
      await deleteCompany(deleteCompanyId);
      toast.success(messageTypes.SUCCESS_DELETE || 'Empresa excluída com sucesso!');
      setDeleteCompanyId(null);
      // Recarregar a lista
      setLoading(true);
      getCompanies({
        page: page,
        per_page: 25,
        stringSearch: filter.stringSearch,
      }).then((data) => {
        if (!data || data === '') {
          setCompanies([]);
          setTotal(0);
          setLoading(false);
          return;
        }

        setTotal(data.data.total || 0);
        setCompanies(data.data.data || []);
        setLoading(false);
        // Ajustar página se necessário
        if (companies.length === 1 && page > 1) {
          setPage(page - 1);
        }
      }).catch(() => {
        setCompanies([]);
        setTotal(0);
        setLoading(false);
      });
    } catch (error) {
      toast.error(error.response?.data?.message || messageTypes.ERROR_DELETE || 'Erro ao excluir empresa');
      setDeleteCompanyId(null);
    }
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <CompanyWrapper>
      <DivDiagonal />
      <ContentArea>
        <PageHeader>
          <h1>
            <i className="fas fa-building"></i>
            Empresas
          </h1>
          <NewCompanyButton as={Link} to="/company/new">
            <i className="fas fa-plus"></i>
            Nova Empresa
          </NewCompanyButton>
        </PageHeader>

        <CompanyCard>
          <FilterSection>
            <StyledFilterFormWrapper>
              <Form ref={formRef} onSubmit={handleSubmit}>
                <FilterTitle>
                  <i className="fas fa-funnel-dollar"/> Filtro
                </FilterTitle>
                <FilterRow>
                  <FilterInputWrapper>
                    <Input
                      type="text"
                      name="stringSearch"
                      placeholder="Pesquisar empresa"
                      label={'Pesquisar empresa'}
                    />
                  </FilterInputWrapper>
                  <FilterButtonWrapper>
                    <CustomButton
                      className="btn btn-primary font-weight-bold text-capitalize mobile-button"
                      type="submit"
                      startIcon={<AiOutlineSearch/>}
                      style={{
                        background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
                        border: 'none',
                        color: '#ffffff',
                        padding: '0 32px',
                        borderRadius: '10px',
                        fontWeight: 600,
                        boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
                        transition: 'all 0.3s ease',
                        width: 'auto',
                        minWidth: 'auto',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(242, 146, 5, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(242, 146, 5, 0.3)';
                      }}
                    >
                      Buscar
                    </CustomButton>
                  </FilterButtonWrapper>
                </FilterRow>
              </Form>
            </StyledFilterFormWrapper>
          </FilterSection>

          <TableWrapper>
            {loading ? (
              <LoadingButton
                className="btn btn-primary text-capitalize font-weight-bold"
                loading={loading}
                type="button"
              >
                Carregando...
              </LoadingButton>
            ) : (
              <>
                {companies && companies.length ? (
                  <StyledTableContainer>
                    <Table sx={{minWidth: 650}} aria-label="caption table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nome</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Funcionários</TableCell>
                          <TableCell align="center">Opções</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {companies.map((company) => (
                          <TableRow key={company.id}>
                            <TableCell>
                              <strong style={{ color: '#ffffff' }}>{company.name}</strong>
                            </TableCell>
                            <TableCell align="center">
                              <span
                                style={{
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  background: company.active
                                    ? 'rgba(76, 175, 80, 0.2)'
                                    : 'rgba(244, 67, 54, 0.2)',
                                  color: company.active ? '#4caf50' : '#f44336',
                                }}
                              >
                                {company.active ? 'Ativa' : 'Inativa'}
                              </span>
                            </TableCell>
                            <TableCell align="center">
                              <Link
                                to={`/operator/${company.id}`}
                                style={{
                                  color: '#00d4ff',
                                  textDecoration: 'none',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#f29205';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = '#00d4ff';
                                }}
                              >
                                <MdPeople />
                                Ver Funcionários
                              </Link>
                            </TableCell>
                            <TableCell align="center">
                              <ActionButtons>
                                <Link to={`/company/${company.id}/edit`}>
                                  <MdEdit />
                                </Link>
                                <button
                                  onClick={() => handleDelete(company.id)}
                                  type="button"
                                >
                                  <MdDeleteForever />
                                </button>
                              </ActionButtons>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      {total > 0 && (
                        <TableFooter>
                          <TableRow>
                            <TableCell colSpan={4}>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '16px',
                                }}
                              >
                                <PaginationInfo>
                                  Mostrando {companies.length} de {total} empresas
                                </PaginationInfo>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <PaginationButton
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                  >
                                    <IoIosArrowBack />
                                  </PaginationButton>
                                  <PaginationButton
                                    onClick={() => setPage(page + 1)}
                                    disabled={companies.length < 25}
                                  >
                                    <IoIosArrowForward />
                                  </PaginationButton>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      )}
                    </Table>
                  </StyledTableContainer>
                ) : (
                  <EmptyState>
                    <MdBusiness />
                    <p>Nenhuma empresa encontrada</p>
                    <span>Cadastre uma nova empresa para começar</span>
                  </EmptyState>
                )}
              </>
            )}
          </TableWrapper>
        </CompanyCard>
      </ContentArea>

      <DeleteCompanyModal
        isOpen={!!deleteCompanyId}
        onClose={() => setDeleteCompanyId(null)}
        onConfirm={handleConfirmDelete}
        companyId={deleteCompanyId}
      />
    </CompanyWrapper>
  );
}

